import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosRequestConfig } from 'axios';

import { isAxiosError } from 'axios';

import type { ServiceRequestConfig } from '~/services/createService';

import { weaver } from '~/services/serviceInstances';

export type WeaverBaseQueryArg = {
  url: string;
  method?: AxiosRequestConfig['method'];
  body?: unknown;
  params?: Record<string, unknown>;
  rtkQueryOptions?: ServiceRequestConfig;
};

const normalizeUrl = (rawUrl: string) => {
  const withoutApiPrefix = rawUrl.replace(/^\/?api\/v1/, '');
  return withoutApiPrefix.startsWith('/')
    ? withoutApiPrefix
    : `/${withoutApiPrefix}`;
};

export const weaverBaseQuery: BaseQueryFn<
  WeaverBaseQueryArg,
  unknown,
  unknown
> = async ({ url, method = 'get', body, params, rtkQueryOptions }) => {
  const path = normalizeUrl(url);
  const config: ServiceRequestConfig = {
    showErrorToast: true,
    ...rtkQueryOptions,
    params,
  };

  try {
    let result: unknown;
    const normalizedMethod = String(method).toLowerCase();
    if (normalizedMethod === 'get') {
      result = await weaver.get(path, config);
    } else if (normalizedMethod === 'post') {
      result = await weaver.post(path, body, config);
    } else if (normalizedMethod === 'patch') {
      result = await weaver.patch(path, body, config);
    } else if (normalizedMethod === 'put') {
      result = await weaver.put(path, body, config);
    } else if (normalizedMethod === 'delete') {
      result = await weaver.remove(path, { ...config, data: body });
    } else {
      return { error: { status: 400, data: `Unsupported method: ${method}` } };
    }
    return { data: result };
  } catch (error) {
    if (isAxiosError(error)) {
      return {
        error: {
          status: error.response?.status ?? 'FETCH_ERROR',
          data: error.response?.data ?? error.message,
        },
      };
    }
    return { error: { status: 'FETCH_ERROR', error: String(error) } };
  }
};
